
import React from 'react';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { ResellerPlanDetails, PlanFeature } from '@/hooks/license/resellerPlans/types';
import FeatureItem from './FeatureItem';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

interface PlanFeaturesProps {
  plan: ResellerPlanDetails;
  onUpdatePlan: (field: keyof ResellerPlanDetails, value: any) => void;
}

const PlanFeatures: React.FC<PlanFeaturesProps> = ({ plan, onUpdatePlan }) => {
  // Initialize empty features array if it doesn't exist
  const features = plan.features || [];
  
  const handleUpdateFeature = (featureIndex: number, updatedFeature: PlanFeature) => {
    console.log("Updating feature:", featureIndex, updatedFeature);
    const updatedFeatures = [...features];
    updatedFeatures[featureIndex] = updatedFeature;
    onUpdatePlan('features', updatedFeatures);
    console.log("Updated features array:", updatedFeatures);
  };

  const handleDeleteFeature = (featureIndex: number) => {
    console.log("Deleting feature:", featureIndex);
    const updatedFeatures = features.filter((_, i) => i !== featureIndex);
    onUpdatePlan('features', updatedFeatures);
    console.log("Features after deletion:", updatedFeatures);
  };

  const handleAddFeature = () => {
    console.log("Adding new feature");
    const newFeature = { name: 'New Feature', included: true };
    const updatedFeatures = [...features, newFeature];
    onUpdatePlan('features', updatedFeatures);
    console.log("Features after adding:", updatedFeatures);
  };

  const handleDragEnd = (result: any) => {
    // Dropped outside the list
    if (!result.destination) return;
    
    // Reorder the features array
    const reorderedFeatures = [...features];
    const [movedFeature] = reorderedFeatures.splice(result.source.index, 1);
    reorderedFeatures.splice(result.destination.index, 0, movedFeature);
    
    // Update the plan with the new feature order
    onUpdatePlan('features', reorderedFeatures);
    console.log("Features after reordering:", reorderedFeatures);
  };

  return (
    <div className="mt-6">
      <h3 className="text-lg font-medium mb-2">Features</h3>
      <p className="text-sm text-gray-500 mb-4">Drag and drop features to reorder them</p>
      
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="features-list">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="space-y-2"
            >
              {features.map((feature, featureIndex) => (
                <Draggable 
                  key={`feature-${featureIndex}`} 
                  draggableId={`feature-${featureIndex}`} 
                  index={featureIndex}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="bg-white border border-gray-200 rounded-md hover:border-primary-300 transition-colors"
                    >
                      <FeatureItem
                        feature={feature}
                        index={featureIndex}
                        onUpdateFeature={handleUpdateFeature}
                        onDeleteFeature={handleDeleteFeature}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      
      <Button 
        variant="outline" 
        onClick={handleAddFeature}
        className="mt-4"
      >
        <Plus className="mr-2 h-4 w-4" />
        Add Feature
      </Button>
    </div>
  );
};

export default PlanFeatures;
